import classNames from 'classnames';
import { ButtonHTMLAttributes, PropsWithChildren } from 'react';
import SpinnerSmall from './SpinnerSmall';

interface ButtonProps extends PropsWithChildren, ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'ghost' | 'success' | 'danger' | 'warning';
  outline?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  spinner?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

function Button({
  children,
  variant = 'primary',
  outline = false,
  size = 'md',
  className,
  spinner = false,
  onClick,
  ...rest
}: ButtonProps) {
  return (
    <button
      onClick={onClick}
      className={classNames(
        'group',
        '',
        'relative inline-flex flex-row items-center justify-center rounded-full border-2 px-6 text-center outline-offset-1 outline-none',
        'text-blue-200 outline-blue-200 focus-visible:border-black focus-visible:outline-4 focus-visible:outline-blue-200',
        'focus-visible:ring-4 focus-visible:ring-offset-2 focus-visible:outline-2 focus-visible:outline-none',
        rest.disabled && 'cursor-not-allowed opacity-35',
        variant === 'primary' &&
          !outline &&
          !rest.disabled &&
          !spinner &&
          'border-blue-700 bg-blue-700 hover:border-blue-900 hover:bg-blue-900 active:bg-blue-900 disabled:border-blue-700/0 disabled:bg-blue-700/25',
        variant === 'primary' &&
          outline &&
          !rest.disabled &&
          !spinner &&
          'border-blue-700 bg-white hover:border-blue-700 hover:bg-blue-700 focus-visible:bg-blue-700 active:bg-blue-700 disabled:border-blue-700/0 disabled:bg-blue-700/25',
        variant === 'primary' &&
          !rest.disabled &&
          spinner &&
          'border-blue-900 bg-blue-900 disabled:border-blue-900/0 disabled:bg-blue-900/25',
        variant === 'primary' && !outline && rest.disabled && 'border-blue-700 bg-blue-700',
        variant === 'primary' && outline && rest.disabled && 'border-blue-700 bg-white',

        variant === 'success' &&
          !outline &&
          !rest.disabled &&
          !spinner &&
          'border-green-600 bg-green-600 hover:border-green-800 hover:bg-green-800 active:bg-green-800 disabled:border-green-600/0 disabled:bg-green-600/25',
        variant === 'success' &&
          outline &&
          !rest.disabled &&
          !spinner &&
          'border-green-600 hover:bg-green-600 focus-visible:bg-green-600',
        variant === 'success' &&
          !rest.disabled &&
          spinner &&
          'border-green-800 bg-green-800 disabled:border-green-800/0 disabled:bg-green-800/25',
        variant === 'success' && !outline && rest.disabled && 'border-green-600 bg-green-600',
        variant === 'success' && outline && rest.disabled && 'border-green-600 bg-white',

        variant === 'warning' &&
          !outline &&
          !rest.disabled &&
          !spinner &&
          'border-yellow-400 bg-yellow-400 hover:border-yellow-500 hover:bg-yellow-500 active:bg-yellow-500 disabled:border-yellow-400/0 disabled:bg-yellow-500/25',
        variant === 'warning' &&
          outline &&
          !rest.disabled &&
          !spinner &&
          'border-yellow-400 hover:bg-yellow-400 focus-visible:bg-yellow-400',
        variant === 'warning' &&
          !rest.disabled &&
          spinner &&
          'border-yellow-500 bg-yellow-500 disabled:border-yellow-500/0 disabled:bg-yellow-500/25',
        variant === 'warning' && !outline && rest.disabled && 'border-yellow-400 bg-yellow-400',
        variant === 'warning' && outline && rest.disabled && 'border-yellow-400 bg-white',

        variant === 'danger' &&
          !outline &&
          !rest.disabled &&
          !spinner &&
          'border-red-600 bg-red-600 hover:border-red-800 hover:bg-red-800 active:bg-red-800 disabled:border-red-600/0 disabled:bg-red-600/25',
        variant === 'danger' &&
          outline &&
          !rest.disabled &&
          !spinner &&
          'border-red-600 bg-white hover:bg-red-600 focus:bg-red-600 focus:outline-red-100 disabled:border-red-600/25 disabled:bg-white',
        variant === 'danger' &&
          !rest.disabled &&
          spinner &&
          'border-red-800 bg-red-800 disabled:border-red-800/0 disabled:bg-red-800/25',
        variant === 'danger' && !outline && rest.disabled && 'border-red-600 bg-red-600',
        variant === 'danger' && outline && rest.disabled && 'border-red-600 bg-white',

        variant === 'ghost' &&
          !rest.disabled &&
          !spinner &&
          'border-transparent bg-transparent hover:bg-neutral-100 focus-visible:bg-neutral-100 disabled:bg-transparent',
        variant === 'ghost' && !rest.disabled && spinner && 'border-transparent bg-neutral-100',
        variant === 'ghost' && rest.disabled && 'border-transparent bg-neutral-100',

        size === 'md' && 'min-h-11 min-w-20 text-sm',
        size === 'sm' && 'min-h-9 !px-4 py-1 text-xs font-normal',
        size === 'lg' && 'min-h-[68px] !p-6 !px-10 text-lg',
        className
      )}
      role={rest.role ?? 'button'}
      {...rest}
    >
      <div
        className={classNames(
          spinner && 'invisible',
          'inline-flex flex-row items-center justify-center',
          variant === 'primary' && !outline && 'text-white',
          variant === 'primary' &&
            outline &&
            !rest.disabled &&
            'text-blue-700 group-hover:text-white group-focus-visible:text-white',
          variant === 'primary' && outline && rest.disabled && 'text-blue-700',

          variant === 'success' && !outline && 'text-white',
          variant === 'success' &&
            outline &&
            !rest.disabled &&
            'text-green-600 group-hover:text-white group-focus:text-white',
          variant === 'success' && outline && rest.disabled && 'text-green-600',

          variant === 'danger' && !outline && 'text-white',
          variant === 'danger' &&
            outline &&
            !rest.disabled &&
            'text-red-600 group-hover:text-white group-focus-visible:text-white disabled:text-red-600/25',
          variant === 'danger' && outline && rest.disabled && 'text-red-600',

          variant === 'warning' && 'text-black',
          variant === 'ghost' &&
            !rest.disabled &&
            'text-blue-700 hover:text-blue-900 focus-visible:text-blue-900',
          variant === 'ghost' && !spinner && 'disabled:text-secondary/25',
          variant === 'ghost' && rest.disabled && 'text-blue-700'
        )}
      >
        {children}
      </div>
      {spinner && (
        <div className="absolute top-0 left-0 flex h-full w-full items-center justify-center">
          <SpinnerSmall dark={variant === 'warning' || variant === 'ghost'} />
        </div>
      )}
    </button>
  );
}

export default Button;
